import React, {
  useCallback, useEffect, useRef, useState
} from "react";
import { atom, useAtomValue } from "jotai";

// region Atoms
import NotificationBell from "@atoms/NotificationBell";
// endregion Atoms
// region Molecules
import NotificationMenu from "@molecules/NotificationMenu";
import NotificationListDialog from "@molecules/NotificationListDialog";
// endregion Molecules
// region Hooks
import { useMyAnnouncements } from "@hooks/useMyAnnouncements";
// endregion Hooks
// region Store
import { MyAnnouncement } from "src/store/graphql/groups/announcement/query.graphql";
// endregion Store

/**
 * Asks the navbar notification center to open its dropdown — e.g. from the
 * Launcher banner's bell. A counter, so every request is a change even when it
 * repeats the previous one.
 */
export const openNotificationsRequestAtom = atom(0);

/**
 * Navbar notification center: the bell, its dropdown and the "view all"
 * dialog. Owns every piece of notification state so the navbar itself stays
 * feature-agnostic; other components ask it to open through
 * {@link openNotificationsRequestAtom}.
 */
const NotificationCenter: React.FC = () => {

  // region States
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [listOpen, setListOpen] = useState(false);
  // endregion States

  const bellRef = useRef<HTMLDivElement>(null);
  const openRequest = useAtomValue(openNotificationsRequestAtom);

  // The bell anchors the dropdown; were it not on screen, the full list opens instead.
  useEffect(() => {
    if (openRequest === 0) return;

    if (bellRef.current) setAnchor(bellRef.current);
    else setListOpen(true);
  }, [openRequest]);

  const {
    announcements, unreadCount, markRead, markAllRead, markReadOnExposure, registerClick
  } = useMyAnnouncements();

  // region Handlers
  const handleCtaClick = useCallback((item: MyAnnouncement) => {
    registerClick(item.id);

    if (item.link) window.open(item.link, "_blank", "noopener,noreferrer");
  }, [registerClick]);

  const handleViewAll = () => {
    setAnchor(null);
    setListOpen(true);
  };
  // endregion Handlers

  return (
    <>
      <NotificationBell ref={bellRef} unreadCount={unreadCount} onClick={(event) => setAnchor(event.currentTarget)} />
      <NotificationMenu
        anchorElement={anchor}
        announcements={announcements}
        unreadCount={unreadCount}
        onClose={() => setAnchor(null)}
        onItemRead={markRead}
        onItemExposed={markReadOnExposure}
        onCtaClick={handleCtaClick}
        onMarkAllRead={markAllRead}
        onViewAll={handleViewAll}
      />
      <NotificationListDialog
        open={listOpen}
        announcements={announcements}
        onClose={() => setListOpen(false)}
        onItemRead={markRead}
        onItemExposed={markReadOnExposure}
        onCtaClick={handleCtaClick}
      />
    </>
  );
};

export default NotificationCenter;
